import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Calculator, Plus, Trash2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  calculatePosition,
  calculationPositionSchema,
  isHourlyLaborLine,
  type CalculationPosition,
  type CalculationTemplate,
} from "@shared/calculation-template";
import type { LVPosition } from "./leistungsverzeichnis-kalkulator";

export interface ProjectPositionCalculationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  position: LVPosition | null;
  onApply: (calculation: CalculationPosition) => void;
}

type DraftState = "idle" | "loading" | "source" | "manual-offer" | "error";
type NumericPath = "quantity" | "laborRate" | "otherRate";

const money = new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" });
const numberText = (value: number) => String(value);
const parseInput = (raw: string): number | null => {
  const value = raw.trim().replace(",", ".");
  if (!/^-?\d+(?:\.\d+)?$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && Math.abs(parsed) <= 1e9 ? parsed : null;
};
function normalizedUnit(unit: string): string | null {
  const value = unit.trim().toLocaleLowerCase("de").replace(/[\s.]/g, "");
  if (!value || value === "ohneeinheit") return null;
  if (value === "m2") return "m²";
  if (value === "m3") return "m³";
  return value;
}
function unitsMatch(first: string, second: string): boolean {
  const a = normalizedUnit(first);
  const b = normalizedUnit(second);
  return !!a && !!b && a === b;
}

function clone<T>(value: T): T {
  return structuredClone(value);
}

function manualDraft(position: LVPosition, code: string, section: CalculationPosition["section"]): CalculationPosition {
  return {
    code: code || position.id,
    name: position.name,
    unit: position.unit,
    section,
    lvPrice: position.ep,
    k7Price: null,
    manualCalculation: true,
    originalLaborCost: 0,
    originalOtherCost: 0,
    originalLineLabor: 0,
    originalLineOther: 0,
    laborMarkup: 0,
    otherMarkup: 0,
    lines: [],
    warnings: ["Manuelle Projektkalkulation: Es liegen keine belegten K7-Ausgangswerte vor."],
  };
}

function parseLegacyId(id: string): { templateId: string; code: string } | null {
  const match = id.match(/^custom-k7-([0-9a-f-]{36})-(.+)$/i);
  return match ? { templateId: match[1], code: match[2] } : null;
}

function SafeNumber({
  label,
  value,
  valueKey,
  invalidText,
  onRawChange,
  min,
  testId,
  disabled = false,
}: {
  label: string;
  value: number;
  valueKey: string;
  invalidText?: string;
  onRawChange: (key: string, raw: string) => void;
  min?: number;
  testId: string;
  disabled?: boolean;
}) {
  const visible = invalidText === undefined ? numberText(value) : invalidText;
  return (
    <div className="min-w-0 space-y-1">
      <Label htmlFor={testId} className="text-[11px] text-muted-foreground">{label}</Label>
      <Input
        id={testId}
        inputMode="decimal"
        autoComplete="off"
        value={visible}
        disabled={disabled}
        aria-invalid={invalidText !== undefined}
        data-testid={testId}
        onChange={(event) => onRawChange(valueKey, event.target.value)}
        className="h-9 px-2 text-right font-mono text-xs tabular-nums"
        min={min}
        maxLength={32}
      />
    </div>
  );
}

export default function ProjectPositionCalculationDialog({
  open,
  onOpenChange,
  position,
  onApply,
}: ProjectPositionCalculationDialogProps) {
  const [draft, setDraft] = useState<CalculationPosition | null>(null);
  const [baseDraft, setBaseDraft] = useState<CalculationPosition | null>(null);
  const [loadState, setLoadState] = useState<DraftState>("idle");
  const [loadMessage, setLoadMessage] = useState("");
  const [templateWarning, setTemplateWarning] = useState(false);
  const [fieldText, setFieldText] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState("");
  const [confirmClose, setConfirmClose] = useState(false);
  const [saving, setSaving] = useState(false);
  const initializedKey = useRef("");
  const activePosition = position as (LVPosition & { calculation?: CalculationPosition }) | null;

  useEffect(() => {
    if (!open) {
      initializedKey.current = "";
      return;
    }
    if (!activePosition) return;
    const key = activePosition.id;
    if (initializedKey.current === key) return;
    initializedKey.current = key;
    setDraft(null);
    setBaseDraft(null);
    setFieldText({});
    setSaveError("");
    setSaving(false);
    setTemplateWarning(false);
    setLoadMessage("");

    if (activePosition.calculation) {
      const snapshot = clone(activePosition.calculation);
      setDraft(snapshot);
      setBaseDraft(clone(snapshot));
      setLoadState("source");
      return;
    }

    const legacy = parseLegacyId(activePosition.id);
    if (!legacy) {
      const manual = manualDraft(activePosition, activePosition.id, "s7");
      setBaseDraft(manual);
      setLoadState("manual-offer");
      setLoadMessage("Für diese Position ist keine K7-Ausgangskalkulation verknüpft. Der Angebots-EP wird nicht als Materialkosten ausgegeben.");
      return;
    }

    let cancelled = false;
    setLoadState("loading");
    apiRequest("GET", `/api/calculation-templates/${legacy.templateId}`)
      .then((response) => response.json() as Promise<CalculationTemplate>)
      .then((template) => {
        if (cancelled) return;
        const source = template.positions.find((item) => item.code === legacy.code);
        if (!source || (source.k7Price === null && !source.manualCalculation)) {
          setLoadState("manual-offer");
          setLoadMessage(!source
            ? "Die Position wurde in der verknüpften Vorlage nicht gefunden. Originale K7-Werte können nicht wiederhergestellt werden."
            : "Die verknüpfte Vorlage enthält für diese Position keine K7-Kalkulation. Originale K7-Werte können nicht wiederhergestellt werden.");
          return;
        }
        const startingPoint = clone(source);
        setDraft(startingPoint);
        setBaseDraft(clone(startingPoint));
        setTemplateWarning(true);
        setLoadState("source");
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        const status = typeof cause === "object" && cause !== null && "status" in cause
          ? String((cause as { status?: unknown }).status)
          : "";
        setLoadState("manual-offer");
        setLoadMessage(status === "404"
          ? "Die verknüpfte Vorlage ist nicht mehr vorhanden. Originale K7-Werte können nicht wiederhergestellt werden."
          : "Die verknüpfte Vorlage konnte nicht geladen werden. Originale K7-Werte können nicht wiederhergestellt werden.");
      });
    return () => { cancelled = true; };
  }, [open, activePosition?.id]);

  const dirty = useMemo(
    () => (Object.keys(fieldText).length > 0) || (!!draft && !!baseDraft && JSON.stringify(draft) !== JSON.stringify(baseDraft)),
    [draft, baseDraft, fieldText],
  );
  const invalidKeys = Object.keys(fieldText);
  const result = useMemo(() => {
    if (!draft) return null;
    const parsed = calculationPositionSchema.safeParse(draft);
    if (!parsed.success) return null;
    try {
      return calculatePosition(parsed.data);
    } catch {
      return null;
    }
  }, [draft]);
  const total = result && activePosition ? result.price * activePosition.qty : null;
  const mismatch = !!draft && !!activePosition && !unitsMatch(draft.unit, activePosition.unit);
  const invalidCalculation = !!draft && (
    !calculationPositionSchema.safeParse(draft).success ||
    !result || !Number.isFinite(result.price) || Math.abs(result.price) > 1e9 ||
    !Number.isFinite(total) || Math.abs(total as number) > 1e9
  );
  const sourceFallback = !!draft && draft.k7Price === null && !draft.manualCalculation;
  const canEdit = !!draft && !sourceFallback;
  const hasHourlyLabor = !!draft?.lines.some(isHourlyLaborLine);
  const laborOverrideActive = draft?.finalHourlyRate !== undefined && hasHourlyLabor;
  const priceOverride = draft?.priceOverride;
  const unitMismatchMessage = mismatch
    ? !normalizedUnit(draft?.unit ?? "") || !normalizedUnit(activePosition?.unit ?? "")
      ? `Einheit fehlt oder ist „ohne Einheit“ (Kalkulation „${draft?.unit ?? ""}“, Projektposition „${activePosition?.unit ?? ""}“). Bitte eine gültige, übereinstimmende Einheit festlegen; ohne Einheit kann nicht übernommen werden.`
      : `Einheit weicht ab: Kalkulation „${draft?.unit}“, Projektposition „${activePosition?.unit}“. Ohne Umrechnung kann diese Kalkulation nicht übernommen werden.`
    : "";

  const requestClose = () => {
    if (dirty) setConfirmClose(true);
    else onOpenChange(false);
  };

  const setRaw = (key: string, raw: string, apply: (value: number) => void, min?: number) => {
    const parsed = parseInput(raw);
    if (parsed === null || (min !== undefined && parsed < min)) {
      setFieldText((current) => ({ ...current, [key]: raw }));
      return;
    }
    setFieldText((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    apply(parsed);
  };

  const updateLine = (lineId: string, field: NumericPath, value: number) => {
    setDraft((current) => current ? {
      ...current,
      lines: current.lines.map((line) => line.id === lineId ? { ...line, [field]: value } : line),
    } : current);
  };

  const addLine = () => {
    setDraft((current) => current ? {
      ...current,
      lines: [...current.lines, {
        id: `project-line-${Date.now()}-${current.lines.length}`,
        code: "",
        name: "Neue Kalkulationszeile",
        formula: "",
        unit: "Stk",
        quantity: 0,
        laborRate: 0,
        otherRate: 0,
        originalQuantity: 0,
        originalLabor: 0,
        originalOther: 0,
        printedRate: 0,
      }],
    } : current);
  };

  const removeLine = (lineId: string) => {
    setDraft((current) => current ? {
      ...current,
      lines: current.lines.filter((line) => line.id !== lineId),
    } : current);
    setFieldText((current) => {
      const next = { ...current };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(`${lineId}.`)) delete next[key];
      });
      return next;
    });
  };

  const initializeManual = () => {
    if (!activePosition) return;
    const manual = manualDraft(activePosition, parseLegacyId(activePosition.id)?.code ?? activePosition.id, "s7");
    setDraft(manual);
    setBaseDraft(clone(manual));
    setFieldText({});
    setLoadState("source");
    setTemplateWarning(false);
  };

  const applyDraft = () => {
    if (!draft || !activePosition) return;
    setSaveError("");
    const parsed = calculationPositionSchema.safeParse(draft);
    if (!parsed.success || invalidKeys.length > 0) {
      setSaveError("Bitte korrigieren Sie alle ungültigen oder leeren Zahlenfelder, bevor Sie übernehmen.");
      return;
    }
    let calculated: ReturnType<typeof calculatePosition>;
    try {
      calculated = calculatePosition(parsed.data);
    } catch {
      setSaveError("Die Kalkulation enthält ungültige Werte und kann nicht berechnet werden.");
      return;
    }
    const projectTotal = calculated.price * activePosition.qty;
    if (!Number.isFinite(calculated.price) || Math.abs(calculated.price) > 1e9 ||
      !Number.isFinite(projectTotal) || Math.abs(projectTotal) > 1e9) {
      setSaveError("Der berechnete Einzel- oder Gesamtpreis ist nicht endlich oder überschreitet 1.000.000.000 €.");
      return;
    }
    if (mismatch) {
      setSaveError(unitMismatchMessage);
      return;
    }
    if (sourceFallback) {
      setSaveError("Diese LV-Position hat keine K7-Kalkulation. Beginnen Sie ausdrücklich eine manuelle Kalkulation.");
      return;
    }
    setSaving(true);
    try {
      onApply(clone(parsed.data));
      onOpenChange(false);
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : "Die Projektkalkulation konnte nicht übernommen werden.");
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => { if (!next) requestClose(); }}>
        <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-5xl" onEscapeKeyDown={(event) => {
          if (dirty) { event.preventDefault(); setConfirmClose(true); }
        }}>
          <DialogHeader className="border-b border-border/70 pb-4">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-bauwerk-teal/25 bg-bauwerk-mint/40 text-bauwerk-dark-teal">
                <Calculator className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <DialogTitle className="text-left">Projektkalkulation anpassen</DialogTitle>
                <DialogDescription className="mt-1 text-left">
                  {activePosition ? <><span className="font-mono text-xs">{activePosition.id}</span> · {activePosition.name}</> : "Position im Bauvorhaben"}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {loadState === "loading" && <div className="space-y-3" aria-label="K7-Ausgangskalkulation wird geladen">
            <div className="h-12 animate-pulse rounded-lg bg-muted" />
            <div className="h-24 animate-pulse rounded-lg bg-muted" />
            <div className="h-24 animate-pulse rounded-lg bg-muted" />
          </div>}

          {loadMessage && <Alert variant="default" className="border-amber-500/40 bg-amber-50/70 text-amber-950">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{loadMessage}
              {loadState === "manual-offer" && <div className="mt-3">
                <Button type="button" size="sm" variant="outline" onClick={initializeManual}>Eigene Kalkulation beginnen</Button>
              </div>}
            </AlertDescription>
          </Alert>}

          {templateWarning && <Alert className="border-amber-500/40 bg-amber-50/70 text-amber-950">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription><strong>Aktuelle Vorlage als Ausgangspunkt.</strong> Historische K7-Werte dieser bereits importierten Position können nicht wiederhergestellt werden. Der bestehende Angebots-EP bleibt unverändert, bis Sie diese Kalkulation ausdrücklich übernehmen.</AlertDescription>
          </Alert>}

          {draft && loadState === "source" && <>
            {draft.manualCalculation && <Alert className="border-amber-500/40 bg-amber-50/70 text-amber-950">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>Manuelle Projektkalkulation: Diese Werte stammen nicht aus einer K7-Kalkulation. Bei einer neu begonnenen Kalkulation starten die Kosten mit 0; der Angebots-EP wird nicht als Materialkosten übernommen. Prüfen und ergänzen Sie die Kosten je Positionseinheit.</AlertDescription>
            </Alert>}
            {sourceFallback && <Alert className="border-amber-500/40 bg-amber-50/70 text-amber-950">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>Für diese Position liegt nur ein LV-Preis vor, keine K7-Kalkulation. Der LV-Fallback bleibt unverändert; Preisänderungen sind erst nach ausdrücklichem Start einer eigenen Kalkulation möglich.
                <div className="mt-3"><Button type="button" size="sm" variant="outline" onClick={initializeManual}>Eigene Kalkulation beginnen</Button></div>
              </AlertDescription>
            </Alert>}
            {unitMismatchMessage && <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>{unitMismatchMessage}</AlertDescription></Alert>}

            {typeof priceOverride === "number" && <Alert className="border-amber-500/40 bg-amber-50/70 text-amber-950">
              <AlertTriangle className="h-4 w-4 text-amber-700" />
              <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                <span>Fixierter Angebotspreis {money.format(priceOverride)} / {draft.unit}; Änderungen an Ansätzen ändern diesen EP erst nach Aufheben der Preisfixierung.</span>
                <Button type="button" size="sm" variant="outline" onClick={() => setDraft((current) => {
                  if (!current) return current;
                  const next = { ...current };
                  delete next.priceOverride;
                  return next;
                })}>Preisfixierung aufheben</Button>
              </AlertDescription>
            </Alert>}

            <section className="grid gap-3 rounded-xl border border-border/80 bg-muted/25 p-4 sm:grid-cols-3" aria-label="Preisübersicht">
              <div><p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Angebot · aktueller EP</p><p className="mt-1 font-mono text-lg tabular-nums">{money.format(activePosition?.ep ?? 0)} <span className="text-xs text-muted-foreground">/ {activePosition?.unit}</span></p></div>
              <div><p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Entwurf · EP je Einheit</p><p className="mt-1 font-mono text-lg font-semibold tabular-nums text-bauwerk-dark-teal">{money.format(result?.price ?? 0)} <span className="text-xs text-muted-foreground">/ {draft.unit}</span></p></div>
              <div><p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Bauvorhaben · {activePosition?.qty} {activePosition?.unit}</p><p className="mt-1 font-mono text-lg tabular-nums">{money.format(total ?? 0)}</p></div>
            </section>

            <p className="text-xs leading-relaxed text-muted-foreground">Alle Zeilen und Sätze unten gelten für <strong>eine Positionseinheit</strong> ({draft.unit}) — nicht für die gesamte Baustellenmenge.</p>

            <section className="grid gap-3 rounded-lg border border-border/70 p-4 sm:grid-cols-3">
              <div className="sm:col-span-3"><h3 className="text-sm font-semibold">Projektbezogene Zuschläge</h3></div>
              <SafeNumber label="Lohnzuschlag · %" value={draft.laborMarkup} valueKey="laborMarkup" invalidText={fieldText.laborMarkup} onRawChange={(key, raw) => setRaw(key, raw, (v) => setDraft((current) => current ? { ...current, laborMarkup: v } : current))} testId="project-labor-markup" disabled={laborOverrideActive} />
              <SafeNumber label="Sonstiges-Zuschlag · %" value={draft.otherMarkup} valueKey="otherMarkup" invalidText={fieldText.otherMarkup} onRawChange={(key, raw) => setRaw(key, raw, (v) => setDraft((current) => current ? { ...current, otherMarkup: v } : current))} testId="project-other-markup" />
              <div className="space-y-1">
                <Label htmlFor="project-final-hourly-rate" className="text-[11px] text-muted-foreground">Endgültiger Verrechnungssatz · €/h</Label>
                <Input id="project-final-hourly-rate" inputMode="decimal" value={fieldText.finalHourlyRate ?? (draft.finalHourlyRate === undefined ? "" : String(draft.finalHourlyRate))} placeholder="K7-Sätze verwenden" aria-invalid={fieldText.finalHourlyRate !== undefined} data-testid="project-final-hourly-rate"
                  onChange={(event) => {
                    const raw = event.target.value;
                    if (!raw.trim()) {
                      setFieldText((current) => { const next = { ...current }; delete next.finalHourlyRate; return next; });
                      setDraft((current) => { if (!current) return current; const next = { ...current }; delete next.finalHourlyRate; return next; });
                    } else setRaw("finalHourlyRate", raw, (v) => setDraft((current) => current ? { ...current, finalHourlyRate: v } : current), 0);
                  }} className="h-9 px-2 text-right font-mono text-xs tabular-nums" />
                <p className="text-[10px] leading-relaxed text-muted-foreground">
                  {laborOverrideActive ? "Aktiv: ersetzt K7-Lohnsatz ohne zusätzlichen Lohnzuschlag." : "Optional; leer bedeutet K7-Sätze."}
                </p>
              </div>
              {laborOverrideActive && <div className="sm:col-span-3 rounded-md border border-amber-500/30 bg-amber-50/60 px-3 py-2 text-xs text-amber-950">Der Lohnzuschlag ist für diese Arbeitszeilen deaktiviert, solange der endgültige Verrechnungssatz aktiv ist. Sonstiges-Zuschlag bleibt wirksam.</div>}
            </section>

            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div><h3 className="text-sm font-semibold">Kalkulationszeilen</h3><p className="text-[11px] text-muted-foreground">Arbeitszeit, Material und Sonstiges je Positionseinheit</p></div>
                <Button type="button" size="sm" variant="outline" disabled={!canEdit} onClick={addLine}><Plus className="mr-1.5 h-3.5 w-3.5" />Zeile hinzufügen</Button>
              </div>
              {draft.lines.length === 0 ? <div className="rounded-lg border border-dashed border-border p-5 text-center text-sm text-muted-foreground">Noch keine Kostenzeilen. Fügen Sie bekannte Arbeits-, Material- oder Gerätekosten je Einheit hinzu.</div>
                : draft.lines.map((line) => <div key={line.id} className="rounded-lg border border-border/80 bg-card p-3">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <Input aria-label="Zeilenbezeichnung" value={line.name} disabled={!canEdit} onChange={(event) => setDraft((current) => current ? { ...current, lines: current.lines.map((item) => item.id === line.id ? { ...item, name: event.target.value } : item) } : current)} className="h-8 border-transparent px-2 font-medium shadow-none focus-visible:border-input" />
                      <Input aria-label="Zeileneinheit" value={line.unit} disabled={!canEdit} onChange={(event) => setDraft((current) => current ? { ...current, lines: current.lines.map((item) => item.id === line.id ? { ...item, unit: event.target.value } : item) } : current)} className="h-8 w-20 shrink-0 px-2 text-center font-mono text-xs" />
                    </div>
                    <Button type="button" variant="ghost" size="icon" aria-label={`Zeile ${line.name} entfernen`} disabled={!canEdit} onClick={() => removeLine(line.id)} className="h-8 w-8 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <SafeNumber label={`Menge · ${line.unit}`} value={line.quantity} valueKey={`${line.id}.quantity`} invalidText={fieldText[`${line.id}.quantity`]} onRawChange={(key, raw) => setRaw(key, raw, (value) => updateLine(line.id, "quantity", value))} testId={`line-${line.id}-quantity`} />
                    <div>
                      <SafeNumber label={laborOverrideActive && isHourlyLaborLine(line) ? "Effektiver Lohnsatz · €/h" : "Lohnsatz · €/ME"} value={laborOverrideActive && isHourlyLaborLine(line) ? draft.finalHourlyRate! : line.laborRate} valueKey={`${line.id}.laborRate`} invalidText={fieldText[`${line.id}.laborRate`]} onRawChange={(key, raw) => setRaw(key, raw, (value) => updateLine(line.id, "laborRate", value))} testId={`line-${line.id}-labor`} disabled={!canEdit || (laborOverrideActive && isHourlyLaborLine(line))} />
                      {laborOverrideActive && isHourlyLaborLine(line) && <p className="mt-1 text-[10px] leading-tight text-muted-foreground">Vom endgültigen Verrechnungssatz gesteuert; K7-Lohnsatz bleibt erhalten.</p>}
                    </div>
                    <SafeNumber label="Material/Sonstiges · €/ME" value={line.otherRate} valueKey={`${line.id}.otherRate`} invalidText={fieldText[`${line.id}.otherRate`]} onRawChange={(key, raw) => setRaw(key, raw, (value) => updateLine(line.id, "otherRate", value))} testId={`line-${line.id}-other`} />
                    <div className="flex items-end justify-end text-right"><div><p className="text-[10px] text-muted-foreground">Zeilenkosten je Einheit</p><p data-testid={`line-${line.id}-preview`} className="font-mono text-xs tabular-nums">{money.format(line.quantity * ((laborOverrideActive && isHourlyLaborLine(line) ? draft.finalHourlyRate! : line.laborRate) + line.otherRate))}</p></div></div>
                  </div>
                </div>)}
            </section>
            {invalidKeys.length > 0 && <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>Mindestens ein Zahlenfeld ist leer oder ungültig. Bitte gültige endliche Werte eingeben; 0 ist ausdrücklich zulässig.</AlertDescription></Alert>}
            {saveError && <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>{saveError}</AlertDescription></Alert>}
            <DialogFooter className="gap-2 border-t border-border/70 pt-4 sm:justify-between">
              <Button type="button" variant="ghost" onClick={requestClose}>Abbrechen</Button>
              <Button type="button" disabled={saving || !canEdit || invalidCalculation || invalidKeys.length > 0 || mismatch} onClick={applyDraft}>
                Für dieses Bauvorhaben übernehmen
              </Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Änderungen verwerfen?</AlertDialogTitle>
            <AlertDialogDescription>Die nicht übernommenen Änderungen an dieser Projektkalkulation gehen verloren. Angebots-EP und gespeicherte Kalkulation bleiben unverändert.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Weiter bearbeiten</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmClose(false); onOpenChange(false); }}>Änderungen verwerfen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
